/**
 * CR307/CR307a: S0 Scan Settings — Web Scan tab
 * Controls: timeout, concurrency, crawler provider, default web recipe.
 * CR307a: Templates-first grouped dropdown + resolution copy.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock, Layers, Globe, FileText, Info, ExternalLink } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { AppScanSettings, RecipeOption } from '@/hooks/useScanSettings';

interface WebScanTabProps {
  settings: AppScanSettings;
  recipes: RecipeOption[];
  updateField: <K extends keyof AppScanSettings>(key: K, value: AppScanSettings[K]) => void;
  disabled?: boolean;
}

export function WebScanTab({ settings, recipes, updateField, disabled = false }: WebScanTabProps) {
  const clampTimeout = (val: number) => Math.max(10, Math.min(600, val || 120));
  const clampConcurrent = (val: number) => Math.max(1, Math.min(10, val || 3));

  // CR307a: Split recipes into templates-first + other
  const { templateRecipes, otherRecipes } = useMemo(() => {
    const tpl: RecipeOption[] = [];
    const other: RecipeOption[] = [];
    for (const r of recipes) {
      (r.is_template ? tpl : other).push(r);
    }
    return { templateRecipes: tpl, otherRecipes: other };
  }, [recipes]);

  return (
    <div className="space-y-6">
      {/* Execution defaults */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Clock className="h-4 w-4" />
          Web Execution Defaults
        </div>

        <p className="text-xs text-muted-foreground">
          Global defaults for web crawl scans. Recipe-level overrides take precedence when set.
        </p>

        <div className="grid grid-cols-2 gap-4">
          {/* Timeout */}
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1.5">
              <Clock className="h-3 w-3" />
              Timeout (seconds)
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-xs">Default timeout used if recipe does not specify a timeout. Range: 10–600 seconds.</p>
                </TooltipContent>
              </Tooltip>
            </Label>
            <Input
              type="number"
              min={10}
              max={600}
              step={10}
              value={settings.web_timeout_seconds}
              onChange={(e) => updateField('web_timeout_seconds', clampTimeout(parseInt(e.target.value)))}
              onBlur={(e) => updateField('web_timeout_seconds', clampTimeout(parseInt(e.target.value)))}
              disabled={disabled}
              className="h-8 text-xs"
            />
            <p className="text-xs text-muted-foreground">Default: 120s</p>
          </div>

          {/* Concurrency */}
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1.5">
              <Layers className="h-3 w-3" />
              Max Concurrent Scans
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-xs">Limits concurrent crawl requests per batch run. Range: 1–10.</p>
                </TooltipContent>
              </Tooltip>
            </Label>
            <Input
              type="number"
              min={1}
              max={10}
              value={settings.web_max_concurrent_scans}
              onChange={(e) => updateField('web_max_concurrent_scans', clampConcurrent(parseInt(e.target.value)))}
              onBlur={(e) => updateField('web_max_concurrent_scans', clampConcurrent(parseInt(e.target.value)))}
              disabled={disabled}
              className="h-8 text-xs"
            />
            <p className="text-xs text-muted-foreground">Default: 3</p>
          </div>
        </div>
      </div>

      {/* Provider + Recipe */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Globe className="h-4 w-4" />
          Web Crawler
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Provider select */}
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1.5">
              Select Web Crawler
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-xs">The crawl provider used for web scans. Additional providers may be added in the future.</p>
                </TooltipContent>
              </Tooltip>
            </Label>
            <Select
              value={settings.web_crawler_provider}
              onValueChange={(v) => updateField('web_crawler_provider', v)}
              disabled={disabled}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="firecrawl">Firecrawl</SelectItem>
                <SelectItem value="playwright">Playwright (stub)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {settings.web_crawler_provider === 'playwright'
                ? 'Playwright is not yet deployed — will fall back to Firecrawl at runtime.'
                : 'Default: Firecrawl'}
            </p>
          </div>

          {/* Default web recipe */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs flex items-center gap-1.5">
                <FileText className="h-3 w-3" />
                Default Web Recipe
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs text-xs">Used when a merchant source does not specify its own web recipe. Select "None" for heuristic crawl.</p>
                  </TooltipContent>
                </Tooltip>
              </Label>
              <Link
                to="/admin/recipes"
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                Open Recipes Studio
                <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
            <Select
              value={settings.web_default_recipe_template_id ?? '__none__'}
              onValueChange={(v) => updateField('web_default_recipe_template_id', v === '__none__' ? null : v)}
              disabled={disabled}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None (heuristic crawl)</SelectItem>
                {templateRecipes.length > 0 && (
                  <SelectGroup>
                    <SelectLabel className="text-xs text-muted-foreground">Templates</SelectLabel>
                    {templateRecipes.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.recipe_name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
                {otherRecipes.length > 0 && (
                  <SelectGroup>
                    <SelectLabel className="text-xs text-muted-foreground">
                      {templateRecipes.length > 0 ? 'Other Recipes' : 'Recipes'}
                    </SelectLabel>
                    {otherRecipes.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.recipe_name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
            {recipes.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No WEB_CRAWL recipes yet —{' '}
                <Link to="/admin/recipes" className="text-primary hover:underline">
                  create one in Recipes Studio
                </Link>.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                This default applies when a WEB source has no override.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Resolution info */}
      <div className="space-y-3 pt-2 border-t border-border/50">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <Info className="h-3 w-3" />
          Effective Resolution (Read-Only)
        </div>
        <div className="p-3 bg-muted/30 rounded-lg text-xs text-muted-foreground">
          <p className="mb-2">For each web scan, timeouts resolve in this order:</p>
          <ol className="list-decimal list-inside space-y-0.5 ml-1">
            <li><strong>Recipe-level</strong> <code>timeout_ms</code> (if set)</li>
            <li><strong>S0 default</strong> <code>web_timeout_seconds</code> (this setting)</li>
          </ol>
          <p className="mt-2">Recipe assignment resolves as:</p>
          <ol className="list-decimal list-inside space-y-0.5 ml-1">
            <li><strong>Merchant source</strong> <code>web_recipe_template_id</code> (CR308)</li>
            <li><strong>S0 default</strong> recipe (this setting)</li>
            <li>Heuristic crawl (no recipe)</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
